import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, HStack, Link, Spinner, Table, Text, VStack } from "@chakra-ui/react";
import dayjs from "dayjs";
import { LeadService } from "@/services/LeadService";
import InteractionStatusBadge from "../InteractionStatusBadge";
import ChannelBadge from "../ChannelBadge";

const extractPhone = (conv) => {
    if (conv.whatsAppId) return conv.whatsAppId;
    if (!conv.collectedSlots) return null;
    try {
        const slots = typeof conv.collectedSlots === 'string' ? JSON.parse(conv.collectedSlots) : conv.collectedSlots;
        return slots.phoneNumber ?? slots.phone ?? null;
    } catch {}
    return null;
};

const extractFromEntities = (conv, name) => {
    if (!conv.collectedAnswers) return null;
    try {
        const entities = typeof conv.collectedAnswers === 'string'
            ? JSON.parse(conv.collectedAnswers)
            : conv.collectedAnswers;
        return entities.find(e => e.name === name)?.value ?? null;
    } catch {}
    return null;
};

// Lighter than candidate/CandidateInteractionsTab.jsx (which wraps the full InteractionList
// filter/search UI) -- GET /lead/{id}/interactions only supports page/pageSize server-side
// (see LeadController.GetInteractionsAsync), so there's nothing to filter against.
const LeadInteractionsTab = ({ leadId }) => {
    const navigate = useNavigate();
    const { id: modelId, campaignId } = useParams();
    const [conversations, setConversations] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(20);
    const [totalCount, setTotalCount] = useState(0);

    useEffect(() => {
        setIsLoading(true);
        LeadService.getInteractions(leadId, page, pageSize)
            .then((data) => {
                setConversations(data?.items ?? []);
                setTotalCount(data?.totalCount ?? 0);
            })
            .finally(() => setIsLoading(false));
    }, [leadId, page, pageSize]);

    const totalPages = Math.ceil(totalCount / pageSize);

    if (isLoading) return <VStack py={8} align="center"><Spinner color="brand.400" size="sm" /></VStack>;

    if (conversations.length === 0) {
        return <VStack py={10} align="center"><Text color="text.grey" fontSize="sm">Brak rozmów</Text></VStack>;
    }

    return (
        <VStack align="stretch" gap={3}>
            <Box border="1px solid" borderColor="border.primary" borderRadius={12} overflowX="auto">
                <Table.Root size="sm" minW="720px">
                    <Table.Header>
                        <Table.Row bg="bg.dark">
                            <Table.ColumnHeader color="text.grey" fontSize="xs" textTransform="uppercase">Imię i nazwisko</Table.ColumnHeader>
                            <Table.ColumnHeader color="text.grey" fontSize="xs" textTransform="uppercase">Status</Table.ColumnHeader>
                            <Table.ColumnHeader color="text.grey" fontSize="xs" textTransform="uppercase">Rozpoczęto</Table.ColumnHeader>
                            <Table.ColumnHeader color="text.grey" fontSize="xs" textTransform="uppercase">Ostatnia aktywność</Table.ColumnHeader>
                            <Table.ColumnHeader color="text.grey" fontSize="xs" textTransform="uppercase">Telefon</Table.ColumnHeader>
                            <Table.ColumnHeader color="text.grey" fontSize="xs" textTransform="uppercase">E-mail</Table.ColumnHeader>
                            <Table.ColumnHeader color="text.grey" fontSize="xs" textTransform="uppercase">Kanał</Table.ColumnHeader>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {conversations.map((conv) => (
                            <Table.Row
                                key={conv.id}
                                cursor="pointer"
                                _hover={{ bg: "bg.highlight" }}
                                onClick={() => navigate(`/models/${modelId}/campaigns/${campaignId}/leads/${leadId}/conversations/${conv.id}`)}
                            >
                                <Table.Cell color="white" fontSize="sm">{extractFromEntities(conv, "@fullName") ?? "-"}</Table.Cell>
                                <Table.Cell><InteractionStatusBadge status={conv.status} /></Table.Cell>
                                <Table.Cell color="white" fontSize="sm">{dayjs(conv.createdAt).format("DD.MM.YYYY HH:mm")}</Table.Cell>
                                <Table.Cell color="text.grey" fontSize="sm">{conv.updatedAt ? dayjs(conv.updatedAt).format("DD.MM.YYYY HH:mm") : "-"}</Table.Cell>
                                <Table.Cell fontSize="sm" onClick={(e) => e.stopPropagation()}>
                                    {extractPhone(conv)
                                        ? <Link href={`tel:${extractPhone(conv)}`} color="white" fontSize="sm">{extractPhone(conv)}</Link>
                                        : <Text color="white" fontSize="sm">-</Text>}
                                </Table.Cell>
                                <Table.Cell color="white" fontSize="sm">{extractFromEntities(conv, "@email") ?? "-"}</Table.Cell>
                                <Table.Cell><ChannelBadge channel={conv.whatsAppId !== null ? "whatsapp" : "chat"} /></Table.Cell>
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table.Root>
            </Box>
            {totalPages > 1 && (
                <HStack justify="flex-end" gap={2}>
                    <Button variant="outlined" size="sm" onClick={() => setPage(p => p - 1)} disabled={page <= 1}>Poprzednia</Button>
                    <Text color="text.grey" fontSize="sm">Strona {page} z {totalPages}</Text>
                    <Button variant="outlined" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages}>Następna</Button>
                </HStack>
            )}
        </VStack>
    );
};

export default LeadInteractionsTab;
